// Cart line identity.
//
// A line is identified by what is being sold AND the unit it is being sold in —
// never by the product alone. A product configured with Piece / Box / Dozen must
// be able to sit on one invoice three times, once per unit, because each of those
// is a different thing to sell at a different price that draws a different amount
// of stock.
//
// Every add-to-cart path used to re-implement this match inline and they disagreed:
// the product tile keyed on prodId + prodType, the variant dialog on varProdId, and
// only the search/scan path included the unit. So a second unit could reach the cart
// only by scanning that unit's own barcode. This module is the single rule.
//
// prodType: 1 = standard, 2 = variant, 3 = composite, 4 = service.

export const PROD_TYPE_SERVICE = 4;

/**
 * Build a comparable identity from any source shape.
 *
 * Call sites map their own object in, because the shapes genuinely differ — the
 * product tile carries `val.id`, the search box carries `val.prodId`, and a cart
 * line carries its base unit nested at `prod.baseUnitId`.
 *
 * `unitId: null` means "the product's base unit", so it is normalised to the
 * explicit base unit id. Without that, a line parked before this feature (null)
 * would refuse to merge with a fresh tile add (which seeds baseUnitId), and the
 * cashier would see two identical Piece lines.
 */
export const cartLineKey = (src) => ({
  prodType: src?.prodType ?? null,
  prodId: src?.prodId ?? null,
  varProdId: src?.varProdId ?? null,
  serviceId: src?.serviceId ?? null,
  unitId: src?.unitId || src?.baseUnitId || null,
});

/** Identity of a line already in the cart. */
export const lineKeyOf = (line) =>
  cartLineKey({
    prodType: line?.prodType,
    prodId: line?.prodId,
    varProdId: line?.varProdId,
    serviceId: line?.serviceId,
    unitId: line?.unitId,
    baseUnitId: line?.prod?.baseUnitId,
  });

export const sameCartLine = (a, b) =>
  !!a &&
  !!b &&
  a.prodType === b.prodType &&
  a.prodId === b.prodId &&
  a.varProdId === b.varProdId &&
  a.serviceId === b.serviceId &&
  a.unitId === b.unitId;

/**
 * May this existing line absorb the incoming one?
 *
 * A weighing-scale label carries its own weight, so two packages of tomatoes are
 * two different quantities and must never collapse into one line.
 *
 * `separateLines` is the outlet's "Add each scan as a separate line" switch
 * (outlet.separateCartLines, read through hooks/useSeparateCartLines). When it is on,
 * nothing ever absorbs anything — not a repeat scan, not a unit change onto a sibling.
 * Every caller passes it here rather than deciding for itself.
 */
export const canMergeInto = (line, key, { separateLines = false } = {}) =>
  !separateLines && !!line && !line.isScaleItem && sameCartLine(lineKeyOf(line), key);

/** Index of the line the incoming item should merge into, or -1. */
export const findMergeIndex = (sellItems, key, opts) =>
  (sellItems || []).findIndex((x) => canMergeInto(x, key, opts));

/**
 * Stock is held per product, not per unit, so a shortage has to be judged on the
 * combined draw of every line of that product converted to base units: 1 Box (20)
 * plus 10 Pieces is 30 against stock, even though neither line looks short alone.
 *
 * Mirrors the server-side grouping in SalesInvoiceRepo.CheckStockShortagesAsync so
 * the counter and the API agree on what "short" means. Cart lines do not carry a
 * maintained baseQty, so it is derived from the conversion factor here.
 */
/**
 * The units a product may be traded in: its base unit first, then every extra
 * unit configured on the product.
 *
 * `filterFor` — "sale" keeps units flagged isForSale, "purchase" keeps
 * isForPurchase, omitted keeps all. Omitted is the no-filter default because
 * `controls/UnitSelect` has always behaved that way and three inventory screens
 * rely on it; the sales screens pass "sale" explicitly.
 *
 * One definition, because the POS cart dropdown, the unit picker dialog and
 * UnitSelect must offer exactly the same list. If they drift, a user can pick a
 * unit the line cannot then display.
 */
export const sellableUnitOptions = (prod, filterFor) => {
  if (!prod?.baseUnitId || !(prod?.productUnits?.length > 0)) return [];
  return [
    {
      id: prod.baseUnitId,
      label: prod.baseUnit
        ? `${prod.baseUnit.name} (${prod.baseUnit.abbreviation})`
        : "Base",
      conversionFactor: 1,
      customPrice: null,
    },
    ...prod.productUnits
      .filter((pu) => {
        if (filterFor === "sale") return pu.isForSale;
        if (filterFor === "purchase") return pu.isForPurchase;
        return true;
      })
      .map((pu) => ({
        id: pu.unitId || pu.unit?.id,
        label: pu.unit
          ? `${pu.unit.name} (${pu.unit.abbreviation})`
          : pu.unitName || (pu.unitId ? `Unit ${pu.unitId}` : "Unit"),
        conversionFactor: pu.conversionFactor,
        customPrice: pu.customPrice,
        minPrice: pu.minPrice,
      })),
  ];
};

/**
 * Price for one unit. A unit may carry its own price (a Box is rarely exactly
 * twenty times a Piece); otherwise it is the base price times the factor. Same
 * rule the backend applies in ProductRepo when a unit barcode is scanned.
 */
export const unitPriceFor = (option, basePrice) =>
  parseFloat(option?.customPrice) > 0
    ? parseFloat(option.customPrice)
    : (parseFloat(basePrice) || 0) * (parseFloat(option?.conversionFactor) || 1);

export const productStockKey = (line) =>
  `${line?.prodType ?? ""}:${line?.prodId ?? ""}:${line?.varProdId ?? ""}`;

export const baseQtyByProduct = (sellItems) => {
  const totals = new Map();
  (sellItems || []).forEach((line) => {
    if (!line || line.prodType === PROD_TYPE_SERVICE) return;
    // Free units draw stock exactly like paid ones — a line selling 20 and giving
    // 2 away takes 22 off the shelf. Counting only the paid half would let the
    // cart pass a shortage check it should fail, by the size of the giveaway.
    const qty = (parseFloat(line.qty) || 0) + (parseFloat(line.freeQty) || 0);
    if (qty <= 0) return;
    const factor = parseFloat(line.conversionFactor) || 1;
    const key = productStockKey(line);
    totals.set(key, (totals.get(key) || 0) + qty * factor);
  });
  return totals;
};

/**
 * A cart line as it goes on the wire: every nested object blanked, FK ids kept.
 *
 * A cart re-hydrated from a saved quotation or Sell/GetById still carries the full
 * prod / unit / service graph each line was loaded with. Posted back, two lines in
 * the same unit became two copies of one Unit and the server refused the save
 * ("The instance of entity type 'Unit' cannot be tracked…"). Every endpoint that
 * takes cart lines — sale, quotation, park — goes through this one list; keep it
 * in step with the Includes in SellRepo.ProgGetDetails and QuoteSaleRepo.GetById.
 */
export const stripLineNavs = (line) => ({
  ...line,
  prod: "",
  varProd: "",
  unit: "",
  service: "",
  employee: "",
  serviceBay: "",
  customerVehicle: "",
  serviceDiscountType: "",
});

/**
 * What a cart gives away: free units and their value at SELLING price — the same
 * basis the server's MaxFreeItemPercent cap uses, so the figure on screen is the one
 * that would be refused. Shared by the POS cart and the Direct Invoice footer.
 */
export const freeGoodsOf = (sellItems) =>
  (sellItems || []).reduce(
    (acc, it) => {
      const free = parseFloat(it?.freeQty) || 0;
      if (free <= 0) return acc;
      return {
        qty: acc.qty + free,
        value: acc.value + free * (parseFloat(it.price) || 0),
      };
    },
    { qty: 0, value: 0 }
  );

/**
 * Units of the base unit one of this line's units holds — 12 for a Box of 12, 1 for a
 * line in the base unit or with no unit at all.
 */
export const lineUnitFactor = (line) => parseFloat(line?.conversionFactor) || 1;

/**
 * The line's minimum selling price and cost, in the unit it is being sold in.
 *
 * MinRtlPrice and SplrPrice are stored per BASE unit, while a line's price is for its own
 * unit, so a Box of 12 must clear twelve times the piece minimum — UNLESS the unit carries
 * its own minimum (product_unit.MinPrice), which is the whole Box's floor and is used as
 * it stands. Never for a variant line, which keeps its own minimum × factor.
 *
 * The ONE floor rule, in three places that must agree: this, SellRepo
 * .ValidateSaleMoneyAuthorityAsync (the server, which decides) and the desktop till's
 * ProductLookup.Build. Change all three together.
 */
const ownUnitMinPrice = (line) => {
  if (line?.varProd || line?.varProdId) return 0;
  const unitId = Number(line?.unitId);
  if (!unitId || unitId === Number(line?.prod?.baseUnitId)) return 0;
  const pu = (line?.prod?.productUnits || []).find(
    (u) => Number(u.unitId || u.unit?.id) === unitId
  );
  return parseFloat(pu?.minPrice) || 0;
};

export const lineMinPrice = (line) => {
  const own = ownUnitMinPrice(line);
  if (own > 0) return own;
  return (
    (parseFloat(line?.varProd?.minRtlPrice || line?.prod?.minRtlPrice || 0) || 0) *
    lineUnitFactor(line)
  );
};

export const lineCostPrice = (line) =>
  (parseFloat(line?.varProd?.splrPrice || line?.prod?.splrPrice || 0) || 0) *
  lineUnitFactor(line);

/**
 * GROSS-entry grids (Direct Invoice) and the server's NET price.
 *
 * SellItem.Price is NET of the line discount — the server books Σ Qty × Price and treats
 * DisAmt as information. The POS cart already keeps a net price. The Direct Invoice grid
 * lets a person type the list price and a discount beside it, so its row.price is GROSS;
 * it posted that unchanged, and the server booked every line discount as if it had not
 * been given (prestigef CRI-137: 20,941 of discounts left on the customer's account).
 *
 * The net price is rounded to the currency's decimals, as the POS does, and EVERY figure
 * the grid shows comes from it — so the screen's total is the server's to the fil.
 * DisAmt is then restated as qty × (gross − net), which is exactly what was taken off.
 */
const roundTo = (v, dp) => {
  const f = Math.pow(10, dp);
  return Math.round(v * f) / f;
};

export const grossLineNetPrice = (line, dp = 3) => {
  const qty = parseFloat(line?.qty) || 0;
  const gross = parseFloat(line?.price) || 0;
  const disAmt = parseFloat(line?.disAmt) || 0;
  if (qty <= 0 || disAmt <= 0) return gross;
  return roundTo(Math.max(0, gross - disAmt / qty), dp);
};

/** A gross grid row as it goes on the wire: net price, DisAmt restated to match. */
export const grossLineToWire = (line, dp = 3) => {
  const qty = parseFloat(line?.qty) || 0;
  const gross = parseFloat(line?.price) || 0;
  const net = grossLineNetPrice(line, dp);
  return { ...line, price: net, disAmt: roundTo(Math.max(0, (gross - net) * qty), dp) };
};

/**
 * A stored line (net price, as every sale and Sales Order line is) back into a gross
 * grid row: the price the discount was taken off. Exact for anything grossLineToWire
 * wrote, because that DisAmt is precisely qty × (gross − net).
 */
export const netLineToGross = (line, dp = 3) => {
  const qty = parseFloat(line?.qty) || 0;
  const net = parseFloat(line?.price) || 0;
  const disAmt = parseFloat(line?.disAmt) || 0;
  if (qty <= 0 || disAmt <= 0) return line;
  return { ...line, price: roundTo(net + disAmt / qty, dp) };
};
